import React, { useState, useEffect, useRef } from "react";
import { MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  User,
  MessageSquare,
} from "lucide-react";
import { validateContactForm, validateFiles } from "../utils/contactValidation";

import Swal from "sweetalert2";
import sendContactRequest from "../services/contactService";

const contactInfo = {
  oilgas: {
    phone: "+57 311 682 8806",
    whatsapp: "573116828806",
    email: "info@sierraalta.com.co",
  },
  ambiental: {
    phone: "+57 350 433 8428",
    whatsapp: "573504338428",
    email: "comercial.laa@sierraalta.com.co",
  },
  otro: {
    phone: "+57 311 682 8806",
    whatsapp: "573116828806",
    email: "info@sierraalta.com.co",
  },
};

const ContactUs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    division: "",
    service: "",
    equipment: "",
    message: "",
    files: [],
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const division = params.get("division");
    const service = params.get("service");
    const equipment = params.get("equipment");

    if (division || service || equipment) {
      setFormData((prev) => ({
        ...prev,
        division: division || "",
        service: service || "",
        equipment: equipment || "",
      }));
    }

    navigate("/contact-us", { replace: true });
  }, [location.search, navigate]);

  const services = {
    oilgas: [
      "Fluidos de perforación",
      "Fluidos de completamiento",
      "Control de sólidos",
      "Consultoría y optimización",
      "Productos químicos",
    ],
    ambiental: [
      "Monitoreo ambiental",
      "Análisis de laboratorio",
      "Consultoría ambiental",
      "Alquiler de equipos",
      "Descarbonización",
    ],
  };

  const equipmentOptions = [
    "Red de ictioplancton",
    "Red de zooplancton",
    "Analizador de óxidos de nitrógeno (NOx)",
    "Multiparámetro portátil avanzado",
    "Red de fitoplancton",
    "Nasa de mano",
    "Analizador de dióxido de azufre (SO₂)",
    "Analizador de combustión PCA 400",
    "Analizador de monóxido de carbono (CO)",
    "Flujómetro portátil de corriente de agua",
    "Red Surber",
    "Analizador de ozono ambiental (O₃)",
    "Colorímetro portátil",
  ];

  const validateField = (name, value) => {
    if (!touched[name] && value.length === 0) return;

    let message = "";

    if (name === "name") {
      if (value.trim() && value.length < 3) message = "Mínimo 3 caracteres";
    }

    if (name === "email") {
      if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        message = "Correo inválido";
      }
    }

    if (name === "phone") {
      if (value.trim() && value.length < 7) message = "Teléfono muy corto";
    }

    if (name === "message") {
      if (value.trim() && value.length < 10) {
        message = "Mínimo 10 caracteres";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: message,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let cleanValue = value;

    if (name === "name") {
      cleanValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");
    }

    if (name === "phone") {
      cleanValue = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: cleanValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    validateField(name, cleanValue);
  };

  // 🔥 CAMBIO IMPORTANTE: SOLO 1 ARCHIVO
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        files: "El archivo supera los 5MB",
      }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      files: null,
    }));

    setFormData((prev) => ({
      ...prev,
      files: [file], // 🔥 se mantiene array para no romper UI
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateContactForm(formData);
    const fileError = validateFiles(formData.files);

    if (fileError) {
      setErrors((prev) => ({
        ...prev,
        files: fileError,
      }));

      return;
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    Swal.fire({
      title: "Enviando solicitud...",
      text: "Por favor espera un momento.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await sendContactRequest(formData);

      Swal.fire({
        icon: "success",
        title: "¡Solicitud enviada!",
        text: "Hemos recibido tu solicitud. Nos pondremos en contacto contigo pronto.",
        confirmButtonColor: "#ee7d32",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        division: "",
        service: "",
        equipment: "",
        message: "",
        files: [],
      });

      setTouched({});
      setErrors({});

      if (formRef.current) {
        formRef.current.reset();
      }
    } catch (error) {
      console.error("Error al enviar formulario:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo enviar",
        text:
          error.message ||
          "Ocurrió un error al enviar la solicitud. Inténtalo nuevamente.",
        confirmButtonColor: "#ee7d32",
      });
    }
  };

  const currentContact = contactInfo[formData.division] || contactInfo.oilgas;

  return (
    <section className="relative overflow-hidden py-10 bg-gradient-to-b from-[#f8f6f3] via-white to-[#f4f7fb]">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#ee7d32]/5 rounded-full blur-[140px]" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[140px]" />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="max-w-4xl mb-16">
          <h2 className="mt-6 text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            Contacto
          </h2>

          <div className="w-24 h-[5px] bg-gradient-to-r from-[#ee7d32] to-orange-400 mt-5 rounded-full" />

          <p className="mt-6 text-xl text-gray-600 leading-relaxed max-w-3xl">
            Hablemos sobre tu próximo proyecto
          </p>
        </div>

        <div
          className="
          overflow-hidden
          rounded-[40px]
          bg-white/80
          backdrop-blur-xl
          border
          border-white/60
          shadow-[0_30px_80px_rgba(0,0,0,0.08)]
          grid
          lg:grid-cols-2
        "
        >
          {/* FORMULARIO */}
          <div className="relative p-8 lg:p-14">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#ee7d32]/5 rounded-full blur-[120px]" />

            <div className="relative z-10 max-w-4xl">
              <h3 className="text-3xl font-bold text-gray-900">
                Solicita información
              </h3>

              <p className="mt-3 text-gray-600">
                Completa el formulario y nos pondremos en contacto contigo.
              </p>

              <form
                ref={formRef}
                method="POST"
                encType="multipart/form-data"
                className="mt-10 space-y-4"
                onSubmit={handleSubmit}
              >
                <div>
                  <div className="relative">
                    <User
                      size={18}
                      className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                    />

                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Nombre completo"
                      className="
      w-full pl-14 pr-6 py-5
      bg-white
      border border-solid border-gray-100
      rounded-3xl
      shadow-sm
      focus:outline-none
      focus:border-[#ee7d32]
      focus:ring-2 focus:ring-[#ee7d32]/15
    "
                    />
                  </div>

                  {errors.name && (
                    <p className="mt-2 text-red-500 text-sm">{errors.name}</p>
                  )}
                </div>

                <div>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                    />

                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Correo electrónico"
                      className="
      w-full pl-14 pr-6 py-5
      bg-white
      border border-solid border-gray-100
      rounded-3xl
      shadow-sm
      focus:outline-none
      focus:border-[#ee7d32]
      focus:ring-2 focus:ring-[#ee7d32]/15
    "
                    />
                  </div>

                  {errors.email && (
                    <p className="mt-2 text-red-500 text-sm">{errors.email}</p>
                  )}
                </div>

                {/* PHONE + DIVISION */}

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <div className="relative">
                      <Phone
                        size={18}
                        className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Teléfono"
                        className="
        w-full pl-14 pr-6 py-5
        bg-white
        border border-solid border-gray-100
        rounded-3xl
        shadow-sm
        focus:outline-none
        focus:border-[#ee7d32]
        focus:ring-2 focus:ring-[#ee7d32]/15
      "
                      />
                    </div>

                    {errors.phone && (
                      <p className="mt-2 text-red-500 text-sm">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <select
                      name="division"
                      value={formData.division}
                      onChange={handleChange}
                      className="
      w-full px-6 py-5
      bg-white
      border border-solid border-gray-100
      rounded-3xl
      shadow-sm
      focus:outline-none
      focus:border-[#ee7d32]
      focus:ring-2 focus:ring-[#ee7d32]/15
      cursor-pointer
    "
                    >
                      <option value="">¿Qué te interesa?</option>
                      <option value="oilgas">Oil & Gas</option>
                      <option value="ambiental">Ambiental</option>
                      <option value="otro">Otro</option>
                    </select>

                    {errors.division && (
                      <p className="text-red-500 text-sm">{errors.division}</p>
                    )}
                  </div>
                </div>

                {/* SERVICE */}
                {formData.division && formData.division !== "otro" && (
                  <div>
                    {" "}
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="
       w-full px-6 py-5
       bg-white
       border border-solid border-gray-100
       rounded-3xl
       shadow-sm
       focus:outline-none
       focus:border-[#ee7d32]
       focus:ring-2 focus:ring-[#ee7d32]/15
       cursor-pointer
     "
                    >
                      {" "}
                      <option value="">Servicio de interés</option>
                      {services[formData.division]?.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                    {errors.service && (
                      <p className="text-red-500 text-sm">{errors.service}</p>
                    )}
                  </div>
                )}

                {/* EQUIPMENT */}
                {formData.division === "ambiental" &&
                  formData.service === "Alquiler de equipos" && (
                    <div>
                      {" "}
                      <select
                        name="equipment"
                        value={formData.equipment}
                        onChange={handleChange}
                        className="
         w-full px-6 py-5
         bg-white
         border border-solid border-gray-100
         rounded-3xl
         shadow-sm
         focus:outline-none
         focus:border-[#ee7d32]
         focus:ring-2 focus:ring-[#ee7d32]/15
         cursor-pointer
       "
                      >
                        {" "}
                        <option value="">¿Qué equipo necesitas? </option>
                        {equipmentOptions.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                      {errors.equipment && (
                        <p className="text-red-500 text-sm">
                          {errors.equipment}
                        </p>
                      )}
                    </div>
                  )}

                {/* MESSAGE */}

                <div>
                  <div className="relative">
                    <MessageSquare
                      size={18}
                      className="absolute left-5 top-5 text-gray-400"
                    />

                    <textarea
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Escribe tu mensaje aquí..."
                      className="
      w-full pl-14 pr-6 py-5
      bg-white
      border border-solid border-gray-100
      rounded-3xl
      resize-none
      shadow-sm
      focus:outline-none
      focus:border-[#ee7d32]
      focus:ring-2 focus:ring-[#ee7d32]/15
    "
                    />
                  </div>

                  {errors.message && (
                    <p className="mt-2 text-red-500 text-sm">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* FILE */}

                <div className="w-full">
                  <label
                    htmlFor="attachment"
                    className="
        relative
        flex flex-col items-center justify-center
        gap-3
        w-full
        p-8
        rounded-3xl
        border-2 border-dashed border-gray-300
        bg-white
        cursor-pointer
        transition-all
        duration-300
        hover:border-[#ee7d32]
        hover:bg-orange-50
        hover:shadow-md
      "
                  >
                    <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                      <svg
                        className="w-7 h-7 text-[#ee7d32]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>

                    <p className="text-sm text-gray-500 text-center">
                      PDF, DOCX, XLSX, JPG o PNG · Máx. 5MB
                    </p>

                    <span className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#ee7d32]">
                      Click para seleccionar archivo
                    </span>

                    <input
                      id="attachment"
                      type="file"
                      name="attachment"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {formData.files?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {formData.files.map((file, i) => (
                          <div
                            key={i}
                            className="
            flex
            items-center
            justify-between
            px-4
            py-2
            bg-gray-50
            rounded-xl
            border
          "
                          >
                            <span className="text-sm text-gray-700">
                              {file.name}
                            </span>

                            <span className="text-xs text-gray-400">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </label>
                </div>

                {errors.files && (
                  <p className="text-red-500 text-sm">{errors.files} </p>
                )}

                {/* SUBMIT */}
                <button
                  type="submit"
                  className="
group
w-full
flex
items-center
justify-center
gap-3
px-8
py-5
rounded-3xl
bg-gradient-to-r
from-[#ee7d32]
to-[#ff9d58]
text-white
font-semibold
shadow-[0_20px_40px_rgba(238,125,50,.35)]
hover:-translate-y-1
hover:scale-[1.01]
transition-all
"
                >
                  Enviar solicitud
                  <ArrowRight
                    size={18}
                    className="
    transition-transform
    duration-300
    group-hover:translate-x-2
  "
                    stroke="white"
                  />
                </button>
              </form>
            </div>
          </div>

          {/* PANEL VISUAL */}
          <div className="relative overflow-hidden">
            <img
              src="/Images/contact-us/contact_us.webp"
              alt="Sierra Alta"
              className="
        absolute
        inset-0
        w-full
        h-full
        object-cover
      "
            />
            <div
              className="
        absolute
        inset-0
        bg-gradient-to-br
        from-black/70
        via-black/55
        to-[#ee7d32]/50
      "
            />
            <div
              className="
        absolute
        top-0
        right-0
        w-72
        h-72
        bg-[#ee7d32]/30
        rounded-full
        blur-[120px]
      "
            />
            <div className="relative z-10 h-full p-8 lg:p-10 flex flex-col">
              <div>
                <h3
                  className="
            mt-6
            text-4xl
            lg:text-5xl
            font-bold
            text-white
            leading-tight
          "
                >
                  Estamos listos para ayudarte
                </h3>

                <p
                  className="
            mt-5
            text-white/80
            text-lg
            leading-relaxed
          "
                >
                  Contáctanos para recibir asesoría especializada en Oil & Gas,
                  gestión ambiental y servicios de laboratorio.
                </p>
              </div>

              <div className="mt-10 space-y-5">
                <div
                  className="
            p-6
            rounded-3xl
            bg-white/10
            backdrop-blur-xl
            border
            border-white/15
            hover:bg-white/15
            transition-all
            duration-300
          "
                >
                  <div className="flex gap-4">
                    <div
                      className="
                w-14
                h-14
                rounded-2xl
                bg-white/10
                flex
                items-center
                justify-center
                flex-shrink-0
              "
                    >
                      <Phone size={22} stroke="white" />
                    </div>

                    <div>
                      <p className="text-white/60 text-sm">WhatsApp</p>

                      <a
                        href={`https://wa.me/${currentContact.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-2 text-white font-semibold text-lg transition-all duration-300 hover:text-[#ee7d32] hover:translate-x-1"
                      >
                        <MessageCircle className="w-5 h-5" stroke="white" />
                        {currentContact.phone}
                      </a>
                    </div>
                  </div>
                </div>

                <div
                  className="
            p-6
            rounded-3xl
            bg-white/10
            backdrop-blur-xl
            border
            border-white/15
            hover:bg-white/15
            transition-all
            duration-300
          "
                >
                  <div className="flex gap-4">
                    <div
                      className="
                w-14
                h-14
                rounded-2xl
                bg-white/10
                flex
                items-center
                justify-center
                flex-shrink-0
              "
                    >
                      <Mail size={22} stroke="white" />
                    </div>

                    <div>
                      <p className="text-white/60 text-sm">
                        Correo electrónico
                      </p>

                      <a
                        href={`mailto:${currentContact.email}`}
                        className="inline-flex items-center mt-2 text-white font-semibold break-all transition-all duration-300 hover:text-[#ee7d32] hover:translate-x-1"
                      >
                        {currentContact.email}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Oficina */}
                <div
                  className="
            p-6
            rounded-3xl
            bg-white/10
            backdrop-blur-xl
            border
            border-white/15
            hover:bg-white/15
            transition-all
            duration-300
          "
                >
                  <div className="flex gap-4">
                    <div
                      className="
                w-14
                h-14
                rounded-2xl
                bg-white/10
                flex
                items-center
                justify-center
                flex-shrink-0
              "
                    >
                      <MapPin size={22} stroke="white" />
                    </div>

                    <div>
                      <p className="text-white font-semibold">
                        Oficina Administrativa
                      </p>

                      <p className="text-white/75 text-sm mt-2 leading-relaxed">
                        Calle 113 #7-45 Oficina 816
                        <br />
                        Bogotá D.C.
                      </p>

                      <a
                        href="https://www.google.com/maps/search/?api=1&query=Calle+113+%237-45+Oficina+816+Bogota+Colombia"
                        target="_blank"
                        rel="noreferrer"
                        className="
                  mt-4
                  inline-flex
                  items-center
                  gap-2
                  text-orange-300
                  font-medium
                  text-sm
                  hover:gap-3
                  transition-all
                "
                      >
                        Ver ubicación
                        <ArrowRight size={14} stroke="#fdba74" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Laboratorio */}
                <div
                  className="
            p-6
            rounded-3xl
            bg-white/10
            backdrop-blur-xl
            border
            border-white/15
            hover:bg-white/15
            transition-all
            duration-300
          "
                >
                  <div className="flex gap-4">
                    <div
                      className="
                w-14
                h-14
                rounded-2xl
                bg-white/10
                flex
                items-center
                justify-center
                flex-shrink-0
              "
                    >
                      <MapPin size={22} stroke="white" />
                    </div>

                    <div>
                      <p className="text-white font-semibold">Laboratorio</p>

                      <p className="text-white/75 text-sm mt-2 leading-relaxed">
                        Calle 141 #47-43
                        <br />
                        Bogotá D.C.
                      </p>

                      <a
                        href="https://www.google.com/maps/search/?api=1&query=Calle+141+%2347-43+Bogota+Colombia"
                        target="_blank"
                        rel="noreferrer"
                        className="
                  mt-4
                  inline-flex
                  items-center
                  gap-2
                  text-orange-300
                  font-medium
                  text-sm
                  hover:gap-3
                  transition-all
                "
                      >
                        Ver ubicación
                        <ArrowRight size={14} stroke="#fdba74" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ```
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
